import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { GetById, Update, GetAllDivisions, GetAllPositions, GetAllSupervisors } from "adminApi/EmployeeApi/Employee";
import Spinner from "Common/Globle/Spinner";
import EditEmployeeForm from "./components/EditEmployeeForm";
import fields from "../json/Employee.json";
import useFormChange from "Hooks/useFormChange";
import useFormSubmit from "Hooks/useFormSubmit";
import { EmployeeRules } from "validation/ValidationRules";
import { validateArrayForm } from "utils/validation";
import useFetchAllData from "Hooks/useFetchAllData";

const EditEmployee = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [loadingData, setLoadingData] = useState(false);


    const buildInitialObject = (fieldList = []) =>
        fieldList.reduce((acc, field) => {
            acc[field.name] = "";
            return acc;
        }, {});

    const initialValues = {
        ...buildInitialObject(fields.basic),
        ...buildInitialObject(fields.position),
        ...buildInitialObject(fields.benefits),
        ...buildInitialObject(fields.class),
        ...buildInitialObject(fields.supervisor),
        ...buildInitialObject(fields.biographical),
        ...buildInitialObject(fields.contacts),
        ...buildInitialObject(fields.emergency),
        custom_fields: []
    };

    const {
        values,
        errors,
        handleChange,
        handleFileChange,
        handleArrayChange,
        setValues,
        setErrors,
    } = useFormChange(initialValues, EmployeeRules);

    const {
        data: DivisionOptions = []
    } = useFetchAllData(
        GetAllDivisions
    );

    const {
        data: PositionOptions = []
    } = useFetchAllData(
        GetAllPositions
    );

    const {
        data: SupervisorOptions = []
    } = useFetchAllData(
        GetAllSupervisors
    );

    useEffect(() => {
        const fetchData = async () => {
            setLoadingData(true);
            const result = await GetById(id);
            if (result.data?.success) {
                setValues({
                    ...initialValues,
                    ...result.data.data,
                    // Ensure arrays are initialized if missing
                    custom_fields: result.data.data.custom_fields || []
                });
            }
            setLoadingData(false);
        };
        if (id) fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const { loading, handleSubmit } = useFormSubmit(

        async (formValues) => {
            if (formValues.enable_login === "false") {
                if (formValues.username) {
                    delete formValues.username;
                }
                if (formValues.password) {
                    delete formValues.password;
                }
            }
            if (typeof formValues?.photograph_url === "string") {
                delete formValues.photograph_url;
            }

            const formData = new FormData();
            Object.entries(formValues).forEach(([k, v]) => {
                if (Array.isArray(v)) {
                    formData.append(k, JSON.stringify(v));
                } else {
                    formData.append(k, v ?? "");
                }
            });

            return await Update({ id, data: formData });
        },
        (vals) => {
            const err = validateArrayForm(vals, EmployeeRules);
            setErrors(err);
            return err;
        },
        () => navigate(-1),
        "Employee updated successfully"
    );

    if (loadingData) return <Spinner isSpinner />;

    return (
        <>
            {loading && <Spinner isSpinner />}
            <EditEmployeeForm
                fields={fields}
                values={values}
                errors={errors}
                handleChange={handleChange}
                handleFileChange={handleFileChange}
                handleArrayChange={handleArrayChange}
                setValues={setValues}
                setErrors={setErrors}
                handleSubmit={(e) => handleSubmit(e, values)}
                loading={loading}
                title="Update Employee"
                rules={EmployeeRules}
                validateArrayForm={validateArrayForm}
                buildInitialObject={buildInitialObject}
                imagePreview={values.photograph_urlPreview || values.photograph_url}
                DivisionOptions={DivisionOptions}
                PositionOptions={PositionOptions}
                SupervisorOptions={SupervisorOptions}
            />
        </>
    );
};

export default EditEmployee;
